import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import {
  EmptyState,
  EmptyStateSizeEnum,
  IconSizeEnum,
  MessageWarningIcon,
} from '@acceso/design-system';
import {
  type RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ReactGridLayout, { getCompactor, useContainerWidth } from 'react-grid-layout';
import { containerBounds } from 'react-grid-layout/core';
import { useTranslation } from 'react-i18next';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { VISIBILITY_OPTIONS } from '@/constants/visibility';
import { useLogosById, usePeopleById } from '@/hooks/use-catalogue-entries-by-id';
import { PresenceTypeEnum } from '@/types/presences';
import { EVisibility } from '@/types/types';

import { GRID_GAP, ITEM_MAX_PX } from '../constants';
import { useTimeKeepingContext } from '../context/TimeKeepingContext';
import { ItemLayoutType, TimeKeepingDashboardHandle } from '../types';
import { buildCompositeKey } from '../utils/composite-key';

import { TimeKeepingLogoCard } from './TimeKeepingLogoCard';
import { TimeKeepingPeopleCard } from './TimeKeepingPeopleCard';

const getCompositeId = (item: Pick<ItemLayoutType, 'presence_type' | 'presence_id'>) =>
  buildCompositeKey(item.presence_type, item.presence_id);

export const BroadcastScheduleProgramBodySegmentToolkitTimeKeepingDashboard = forwardRef<
  TimeKeepingDashboardHandle,
  {}
>(function BroadcastScheduleProgramBodySegmentToolkitTimeKeepingDashboard(_props, ref) {
  const { t } = useTranslation();
  const { items, onColsChange, onUpdateRecord } = useTimeKeepingContext();
  const { byId: logosById } = useLogosById(
    items
      .filter(item => item.presence_type === PresenceTypeEnum.LOGOS)
      .map(item => item.presence_id),
    { keepPrevious: true }
  );
  const { byId: peopleById } = usePeopleById(
    items
      .filter(item => item.presence_type !== PresenceTypeEnum.LOGOS)
      .map(item => item.presence_id),
    { keepPrevious: true }
  );

  const { width, containerRef, mounted } = useContainerWidth();
  const layoutRef = useRef<{ i: string; x: number; y: number; w: number; h: number }[]>([]);

  useImperativeHandle(ref, () => ({
    getLayout: () => layoutRef.current,
    getCols: () => {
      const el = containerRef.current as HTMLElement | null;
      const W = el?.getBoundingClientRect().width ?? 0;
      return W > 0 ? Math.max(1, Math.ceil((W + GRID_GAP) / (ITEM_MAX_PX + GRID_GAP))) : 1;
    },
  }));

  const compactor = useMemo(() => getCompactor(null, true), []);
  const cols = Math.max(1, Math.ceil((width + GRID_GAP) / (ITEM_MAX_PX + GRID_GAP)));

  useEffect(() => {
    onColsChange(cols);
  }, [cols, onColsChange]);

  const [stackOrder, setStackOrder] = useState<string[]>(() => items.map(getCompositeId));

  useEffect(() => {
    setStackOrder(prev => {
      const ids = new Set(items.map(getCompositeId));
      const kept = prev.filter(id => ids.has(id));
      const added = items.map(getCompositeId).filter(id => !prev.includes(id));
      if (added.length === 0 && kept.length === prev.length) return prev;
      return [...kept, ...added];
    });
  }, [items]);

  const bringToTop = useCallback((id: string) => {
    setStackOrder(prev => (prev.at(-1) === id ? prev : [...prev.filter(k => k !== id), id]));
  }, []);

  const [openVisibility, setOpenVisibility] = useState<{
    key: string;
    compositeId: string;
    supportPresenceId: string;
    recordIndex: number;
    currentVisibility: EVisibility;
    coords: { top: number; left: number };
  } | null>(null);

  const handleToggleVisibility = useCallback(
    (
      compositeId: string,
      supportPresenceId: string,
      recordIndex: number,
      currentVisibility: EVisibility,
      el: HTMLElement
    ) => {
      const key = `${compositeId}:${supportPresenceId}:${recordIndex}`;
      setOpenVisibility(prev => {
        if (prev?.key === key) return null;
        const rect = el.getBoundingClientRect();
        return {
          key,
          compositeId,
          supportPresenceId,
          recordIndex,
          currentVisibility,
          coords: { top: rect.bottom + 4, left: rect.left },
        };
      });
    },
    []
  );

  const handleVisibilityChange = useCallback(
    (selected: ListboxSelected) => {
      if (!openVisibility) return;
      onUpdateRecord(
        openVisibility.compositeId,
        openVisibility.supportPresenceId,
        openVisibility.recordIndex,
        { visibility: selected[0] as EVisibility }
      );
      setOpenVisibility(null);
    },
    [openVisibility, onUpdateRecord]
  );

  const handleInteractionStart = useCallback(
    (_layout: unknown, _oldItem: unknown, newItem: unknown) => {
      if (newItem && typeof newItem === 'object' && 'i' in newItem) {
        bringToTop((newItem as { i: string }).i);
      }
    },
    [bringToTop]
  );

  const rglItems = useMemo(
    () =>
      items.map(item => {
        const w = Math.min(item.w, cols);
        return {
          ...item,
          i: getCompositeId(item),
          w,
          minW: w,
          maxW: w,
          minH: item.min_h,
        };
      }),
    [items, cols]
  );

  if (rglItems.length === 0) {
    return (
      <div className="h-full w-full overflow-x-auto scrollbar-gutter-stable">
        <div className="m-auto flex min-h-96 w-full flex-col items-center justify-center gap-size-8 p-size-24">
          <EmptyState
            icon={<MessageWarningIcon size={IconSizeEnum.CUSTOM} />}
            size={EmptyStateSizeEnum.MD}
            title={t('No hay presencias registradas') as string}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-id="grid-layout-container" ref={containerRef as RefObject<HTMLDivElement>}>
      {mounted && (
        <ReactGridLayout
          compactor={compactor}
          layout={rglItems}
          width={width}
          gridConfig={{ cols, rowHeight: 30, margin: [GRID_GAP, GRID_GAP] }}
          constraints={[containerBounds]}
          onDragStart={handleInteractionStart}
          onResizeStart={handleInteractionStart}
          onLayoutChange={layout => {
            layoutRef.current = (layout as any[]).map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
          }}
          dragConfig={{ handle: '.card__widget__header' }}
        >
          {items.map(item => {
            const compositeId = getCompositeId(item);
            const z = stackOrder.indexOf(compositeId) + 1;
            if (item.presence_type === PresenceTypeEnum.LOGOS) {
              return (
                <div key={compositeId}>
                  <TimeKeepingLogoCard
                    item={item}
                    logo={logosById.get(item.presence_id)}
                    z={z}
                    onBringToTop={bringToTop}
                    onOpenVisibility={handleToggleVisibility}
                  />
                </div>
              );
            }
            return (
              <div key={compositeId}>
                <TimeKeepingPeopleCard
                  item={item}
                  person={peopleById.get(item.presence_id)}
                  z={z}
                  onBringToTop={bringToTop}
                  onOpenVisibility={handleToggleVisibility}
                />
              </div>
            );
          })}
        </ReactGridLayout>
      )}
      {openVisibility !== null &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[101]" onPointerDown={() => setOpenVisibility(null)} />
            <div
              className="fixed z-[102] min-w-[120px] border border-neutral-20 bg-white p-3 shadow-lg"
              style={{ top: openVisibility.coords.top, left: openVisibility.coords.left }}
            >
              <Listbox
                dark={false}
                mode="single"
                items={VISIBILITY_OPTIONS}
                onChange={handleVisibilityChange}
                identifiers={{ key: 'value', value: 'value', label: 'label' }}
                selected={[openVisibility.currentVisibility]}
                onClose={() => setOpenVisibility(null)}
              />
            </div>
          </>,
          document.body
        )}
    </div>
  );
});
